import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { Menu, X, ArrowRight, Sun, Moon } from "lucide-react";

const NAV = [
  { key: "modules", href: "/#modules", id: "modules" },
  { key: "pricing", href: "/tarification", id: "tarification" },
  { key: "ecosystem", href: "/#ecosysteme", id: "ecosysteme" },
  { key: "plans", href: "/#forfaits", id: "forfaits" },
  { key: "demo", href: "/#demo", id: "demo" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, lang, toggleLang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const overDarkHero = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSpace = () => navigate(user ? "/app" : "/login");

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        overDarkHero ? "pg-on-dark " : ""
      }${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-[70px] flex items-center justify-between">
        <Logo size={38} />

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.id}`}
              className="text-sm text-white/70 hover:text-[#EAB308] transition-colors font-medium"
            >
              {t(`nav.${n.key}`)}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            aria-label={lang === "fr" ? "Switch to English" : "Passer en français"}
            title={lang === "fr" ? "English" : "Français"}
            className="flex h-8 items-center rounded-full px-2.5 text-xs font-bold tracking-wide text-white/60 hover:text-[#EAB308] hover:bg-white/5 transition-colors font-mono"
          >
            {lang === "fr" ? "FR" : "EN"}<span className="mx-1 text-white/25">/</span><span className="text-white/35">{lang === "fr" ? "EN" : "FR"}</span>
          </button>
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label={theme === "light" ? "Activer le mode sombre" : "Activer le mode clair"}
            title={theme === "light" ? "Mode sombre" : "Mode clair"}
            className="flex h-8 w-8 items-center justify-center rounded-full text-white/50 hover:text-[#EAB308] hover:bg-white/5 transition-colors"
          >
            {theme === "light" ? <Moon className="w-[18px] h-[18px]" /> : <Sun className="w-[18px] h-[18px]" />}
          </button>
          <Button
            data-testid="header-space-btn"
            onClick={goSpace}
            className="bg-[#EAB308] text-black font-semibold hover:opacity-90 rounded-md gap-2 transition-opacity"
          >
            {user ? t("nav.mySpace") : t("nav.space")}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="md:hidden flex items-center gap-0.5">
          <button
            data-testid="lang-toggle-mobile"
            onClick={toggleLang}
            aria-label={lang === "fr" ? "Switch to English" : "Passer en français"}
            className="flex h-8 items-center rounded-full px-2 text-xs font-bold text-white/70 font-mono"
          >
            {lang === "fr" ? "FR" : "EN"}
          </button>
          <button
            data-testid="theme-toggle-mobile"
            onClick={toggle}
            aria-label={theme === "light" ? "Activer le mode sombre" : "Activer le mode clair"}
            className="flex h-8 w-8 items-center justify-center rounded-full text-white/60"
          >
            {theme === "light" ? <Moon className="w-[18px] h-[18px]" /> : <Sun className="w-[18px] h-[18px]" />}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="text-white p-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-5 py-6 flex flex-col gap-4">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-[#EAB308] transition-colors py-1"
            >
              {t(`nav.${n.key}`)}
            </a>
          ))}
          <Button
            data-testid="mobile-space-btn"
            onClick={() => {
              setOpen(false);
              goSpace();
            }}
            className="bg-[#EAB308] text-black font-semibold w-full mt-2"
          >
            {user ? t("nav.mySpace") : t("nav.space")}
          </Button>
        </div>
      )}
    </header>
  );
}
